/**
 * Timbrado local (Web Audio) mientras el destino no contesta.
 *
 * - **pe_latam:** al **empezar** el timbrado, **una sola vez** tres tonos **tipo marcador** (DTMF teclas 1–2–3,
 *   ITU-T Q.23: frecuencias distintas, sonido “bonito” y familiar en Perú/Latam); luego solo el timbre **largo**
 *   a **425 Hz** (patrón cercano al tono de progreso ETSI / muchas PSTN en la región).
 * - **us_compact:** 440+480 Hz, pulso breve + pausa (EE.UU.).
 *
 * ### Investigación (mercado peruano, 2025)
 * - **OSIPTEL / ITU-T E.180:** no hay en fuentes abiertas un PDF único con Hz y cadencia del “sonidito” que
 *   algunos operadores móviles insertan al marcar; el timbrado **fijo** en Latam suele alinearse a **425 Hz**
 *   (Wikipedia “Ringing tone”, ETSI; Ecuador en bases tipo World Tone Database ~1,2s/4,65s como referencia vecina).
 * - **Percepción de usuarios (p. ej. foros Perú Hardware):** Movistar/Claro se asocian a **tonos distintivos**
 *   al originar la llamada; no replicamos una marca concreta (sería propietario), sino un **análogo audible**:
 *   tres pulsos **como el teclado** (1-2-3), que suenan distintos entre sí y recuerdan al “marcador”.
 */
import {
  getRingbackOnlyAudioContext,
  getSharedAudioContext,
  resumeAudioContextWithRetries,
} from '@/lib/dtmfKeypad';
import { playRingbackHtmlFallbackPulse } from '@/lib/ringbackHtmlFallback';

const PEAK_GAIN = 0.075;

export type RingbackPresetId = 'us_compact' | 'pe_latam';

export const RINGBACK_PRESET_STORAGE_KEY = 'call_center_ringback_preset';

/**
 * Si el timbrado mientras esperas debe generarse en el navegador (Web Audio) o no.
 * `false` = solo lo que envíe Telnyx por WebRTC; en muchos bridges **no hay audio** hasta que contesta el destino o el buzón.
 */
export const SYNTHETIC_RINGBACK_STORAGE_KEY = 'call_center_synthetic_ringback';

/**
 * Modo de timbrado:
 * - `'auto'`  (defecto): sintético como fallback, se calla cuando Telnyx envía audio real (early media).
 * - `'1'`     : sintético forzado (comportamiento legacy, siempre genera tono local).
 * - `'0'`     : solo audio de Telnyx (puede oírse silencio si no hay early media).
 */
export type RingbackMode = 'auto' | '1' | '0';

export function getRingbackMode(): RingbackMode {
  if (typeof window === 'undefined') return 'auto';
  const env = import.meta.env.VITE_DISABLE_LOCAL_RINGBACK?.toLowerCase().trim() ?? '';
  if (env === 'true' || env === '1') return '0';
  if (env === 'false' || env === '0') return '1';
  try {
    const raw = localStorage.getItem(SYNTHETIC_RINGBACK_STORAGE_KEY)?.trim().toLowerCase() ?? '';
    if (raw === '0' || raw === 'false') return '0';
    if (raw === '1' || raw === 'true') return '1';
    return 'auto';
  } catch {
    return 'auto';
  }
}

/**
 * `getUseSyntheticRingback()` retorna `true` si el sintético debe poder sonar
 * (modos `'auto'` y `'1'`). En modo `'0'` (solo Telnyx) retorna `false`.
 */
export function getUseSyntheticRingback(): boolean {
  return getRingbackMode() !== '0';
}

/**
 * `true` en modo `'auto'`: el detector de early media decide cuándo callar el sintético.
 * En modo `'1'` (forzado) el sintético suena siempre, ignorando early media.
 */
export function isEarlyMediaAutoMode(): boolean {
  return getRingbackMode() === 'auto';
}

export function setRingbackMode(mode: RingbackMode): void {
  try {
    localStorage.setItem(SYNTHETIC_RINGBACK_STORAGE_KEY, mode);
  } catch {
    // ignore
  }
}

/** @deprecated Usa `setRingbackMode` en su lugar. */
export function setUseSyntheticRingback(enabled: boolean): void {
  setRingbackMode(enabled ? '1' : '0');
}

/** Opciones para Select en UI (orden: Perú primero). */
export const RINGBACK_PRESET_SELECT_OPTIONS: { label: string; value: RingbackPresetId }[] = [
  {
    value: 'pe_latam',
    label: 'Perú / Latinoamérica (3 tonos tipo marcador + timbre largo)',
  },
  {
    value: 'us_compact',
    label: 'Estilo EE.UU. (440+480 Hz, tono breve)',
  },
];

/** Un solo pulso + silencio (preset EE.UU.). */
const US_COMPACT = {
  freqsHz: [440, 480],
  burstSec: 0.88,
  pauseSec: 2.0,
} as const;

/**
 * Perú / Latam: trío inicial tipo **marcador DTMF** (una sola vez por sesión) + ciclos de timbre largo 425 Hz.
 */
const PE = {
  freqsHz: [425] as const,
  /** Duración de cada pulsación “tipo tecla” al inicio. */
  introDtmfSec: 0.1,
  /** Silencio entre pulsos del trío. */
  introGapSec: 0.05,
  /** Pausa antes del primer timbre largo (tras el trío). */
  gapBeforeLongSec: 0.13,
  /** Timbre largo “central” (se repite en cada ciclo tras el inicio). */
  longTuuSec: 1.08,
  /** Silencio después de cada timbre largo hasta el siguiente. */
  pauseAfterLongSec: 3.65,
} as const;

/**
 * ITU-T Q.23 — misma fila del teclado (697 Hz), columnas ascendentes: suena como 1-2-3 del marcador.
 */
const PE_INTRO_DTMF: readonly (readonly [number, number])[] = [
  [697, 1209],
  [697, 1336],
  [697, 1477],
] as const;

/** Solo audio del trío (sin el largo). */
const PE_TRIO_AUDIO_SEC =
  3 * PE.introDtmfSec + 2 * PE.introGapSec + PE.gapBeforeLongSec;

/** Primer ciclo: trío + largo + pausa (hasta que empiece el siguiente largo). */
const PE_FIRST_CYCLE_SEC = PE_TRIO_AUDIO_SEC + PE.longTuuSec + PE.pauseAfterLongSec;

/** Ciclos siguientes: largo + pausa. */
const PE_LONG_CYCLE_SEC = PE.longTuuSec + PE.pauseAfterLongSec;

export function getUsCompactCycleMs(): number {
  return Math.round((US_COMPACT.burstSec + US_COMPACT.pauseSec) * 1000);
}

/** Tiempo hasta el siguiente tick tras el **primer** ciclo (incluye trío + largo + silencio). */
export function getPeLatamFirstCycleMs(): number {
  return Math.round(PE_FIRST_CYCLE_SEC * 1000);
}

/** Tiempo entre timbres largos consecutivos. */
export function getPeLatamLongCycleMs(): number {
  return Math.round(PE_LONG_CYCLE_SEC * 1000);
}

/**
 * Compatibilidad: para `us_compact` = ciclo único; para `pe_latam` = ciclo largo (el más frecuente tras el inicio).
 */
export function getRingbackRepeatMs(): number {
  return getRingbackPreset() === 'us_compact' ? getUsCompactCycleMs() : getPeLatamLongCycleMs();
}

/**
 * Sin valor guardado → **pe_latam** (mercado Perú por defecto en Smarty).
 * Valores aceptados en localStorage: `pe_latam`, `peru`, `latam` | `us_compact`, `us`.
 */
export function getRingbackPreset(): RingbackPresetId {
  if (typeof window === 'undefined') return 'pe_latam';
  try {
    const v = localStorage.getItem(RINGBACK_PRESET_STORAGE_KEY)?.trim().toLowerCase() ?? '';
    if (v === 'us_compact' || v === 'us') return 'us_compact';
    if (v === 'pe_latam' || v === 'peru' || v === 'latam') return 'pe_latam';
    return 'pe_latam';
  } catch {
    return 'pe_latam';
  }
}

export function setRingbackPreset(id: RingbackPresetId): void {
  try {
    localStorage.setItem(RINGBACK_PRESET_STORAGE_KEY, id);
  } catch {
    // ignore
  }
}

/** Timbre largo con ataque/sostenido/release suaves. */
function scheduleLongBurst(
  ctx: AudioContext,
  tStart: number,
  durationSec: number,
  freqsHz: readonly number[]
): void {
  const end = tStart + durationSec;
  const gain = ctx.createGain();
  gain.gain.setValueAtTime(0.0001, tStart);
  gain.gain.exponentialRampToValueAtTime(PEAK_GAIN, tStart + 0.022);
  gain.gain.setValueAtTime(PEAK_GAIN, end - 0.055);
  gain.gain.exponentialRampToValueAtTime(0.0001, end);

  for (const freq of freqsHz) {
    const osc = ctx.createOscillator();
    osc.type = 'sine';
    osc.frequency.value = freq;
    osc.connect(gain);
    osc.start(tStart);
    osc.stop(end + 0.02);
  }

  gain.connect(ctx.destination);
}

/**
 * Par DTMF (dos tonos), envolvente tipo tecla de teléfono — más “marcador” que un solo seno a 425 Hz.
 */
function scheduleDtmfIntroBurst(
  ctx: AudioContext,
  tStart: number,
  durationSec: number,
  lowHz: number,
  highHz: number
): void {
  const end = tStart + durationSec;
  const master = ctx.createGain();
  /** Dos osciladores: ganancia menor que el timbre largo para evitar saturación. */
  const peak = PEAK_GAIN * 0.52;
  const attack = 0.003;
  master.gain.setValueAtTime(0.0001, tStart);
  master.gain.exponentialRampToValueAtTime(peak, tStart + attack);
  master.gain.exponentialRampToValueAtTime(0.0001, end);

  for (const freq of [lowHz, highHz]) {
    const osc = ctx.createOscillator();
    osc.type = 'sine';
    osc.frequency.value = freq;
    osc.connect(master);
    osc.start(tStart);
    osc.stop(end + 0.018);
  }
  master.connect(ctx.destination);
}

function playUsCompact(ctx: AudioContext, t0: number): void {
  scheduleLongBurst(ctx, t0, US_COMPACT.burstSec, US_COMPACT.freqsHz);
}

/** Trío tipo teclas 1-2-3 + primer timbre largo (primera vez en la sesión de timbrado). */
function playPeIntroPlusLong(ctx: AudioContext, t0: number, introOnly = false): void {
  let t = t0;
  for (let i = 0; i < PE_INTRO_DTMF.length; i++) {
    const [lo, hi] = PE_INTRO_DTMF[i]!;
    scheduleDtmfIntroBurst(ctx, t, PE.introDtmfSec, lo, hi);
    t += PE.introDtmfSec + (i < PE_INTRO_DTMF.length - 1 ? PE.introGapSec : 0);
  }
  if (introOnly) return;
  t += PE.gapBeforeLongSec;
  scheduleLongBurst(ctx, t, PE.longTuuSec, PE.freqsHz);
}

/** Solo timbre largo (cada ciclo después del inicio). */
function playPeLongOnly(ctx: AudioContext, t0: number): void {
  scheduleLongBurst(ctx, t0, PE.longTuuSec, PE.freqsHz);
}

/**
 * Timbrado: 1) contexto dedicado (micrófono), 2) compartido DTMF, 3) `<audio>`+WAV si el navegador sigue bloqueando Web Audio.
 */
function runWithContext(run: (ctx: AudioContext, t0: number) => void): void {
  void (async () => {
    const candidates = [getRingbackOnlyAudioContext(), getSharedAudioContext()].filter(
      (c): c is AudioContext => c != null && c.state !== 'closed',
    );

    for (const ctx of candidates) {
      await resumeAudioContextWithRetries(ctx, 14, 45);
      if (ctx.state === 'running') {
        const t0 = ctx.currentTime + 0.02;
        run(ctx, t0);
        return;
      }
    }

    const fb = getRingbackPreset() === 'us_compact' ? 'us_short' : 'pe_long';
    playRingbackHtmlFallbackPulse(fb);
  })();
}

/** Preset EE.UU. (sin fases). */
export function playUsCompactPulse(): void {
  runWithContext(playUsCompact);
}

/** Perú: primer ciclo con trío + largo. `introOnly` = solo los 3 puntitos sin el "tuuu". */
export function playPeLatamIntroPlusLong(introOnly = false): void {
  runWithContext((ctx, t0) => playPeIntroPlusLong(ctx, t0, introOnly));
}

/** Perú: solo timbre largo. */
export function playPeLatamLongPulse(): void {
  runWithContext(playPeLongOnly);
}

/**
 * Un pulso según preset (sin lógica de “solo una vez” el trío — usa el bucle en `callRingbackLoop`).
 * Para `pe_latam` reproduce **solo** el largo (compatibilidad mínima).
 */
export function playLocalRingbackPulse(): void {
  if (getRingbackPreset() === 'us_compact') {
    playUsCompactPulse();
  } else {
    playPeLatamLongPulse();
  }
}
